import {
  readQuantity,
  type QuantityProblem,
  type QuantityUnit,
} from '@kk-pos/shared/purchasing';

/**
 * The console side of typed purchase quantities (TASK-0228). Screens keep the
 * raw text of every quantity box; these turn that text into the reason an
 * action is disabled, and into the number a request carries. A blank or
 * unreadable box is never sent, and never sent as 0.
 */

/**
 * "-1" and the like: a minus sign in front of what would otherwise read as a
 * quantity. The shared reader refuses every sign as not a number; the operator
 * is told the actual reason. Seen on invoice credit lines (import review).
 */
function isNegative(text: string, unit: QuantityUnit): boolean {
  const trimmed = text.trim();
  if (!trimmed.startsWith('-')) return false;
  const rest = readQuantity(trimmed.slice(1), unit);
  return rest.ok || (rest.problem !== 'blank' && rest.problem !== 'not_a_number');
}

function phrase(problem: QuantityProblem, unit: QuantityUnit, text: string): string {
  switch (problem) {
    case 'blank':
      return 'is blank';
    case 'not_whole':
      return unit === 'pack' ? 'takes whole cases only' : 'takes whole numbers only';
    case 'too_many_decimals':
      return 'takes at most 3 decimals';
    case 'too_large':
      return 'is too large';
    case 'not_a_number':
      return isNegative(text, unit)
        ? 'cannot be negative'
        : `"${text.trim()}" is not a number`;
  }
}

export type QuantityCheck =
  | { quantity: number; problem: null }
  | { quantity: null; problem: string };

/**
 * Read a box's text. `problem` finishes a sentence that starts with the box's
 * name: "Ground beef: quantity " + "is blank".
 */
export function checkQuantity(text: string, unit: QuantityUnit): QuantityCheck {
  const reading = readQuantity(text, unit);
  return reading.ok
    ? { quantity: reading.value, problem: null }
    : { quantity: null, problem: phrase(reading.problem, unit, text) };
}

/**
 * The one conversion a request makes from a box's text. Throws instead of
 * returning a fallback, so a box that slipped past the disabled action still
 * cannot reach the API as 0.
 */
export function quantityForRequest(
  text: string,
  unit: QuantityUnit,
  label: string
): number {
  const check = checkQuantity(text, unit);
  if (check.problem !== null) {
    throw new Error(`${label}: quantity ${check.problem}. Nothing was sent.`);
  }
  return check.quantity;
}
